/**
 * Static Canada province / territory → cities list provided by the client for Opportunities filters.
 * Dropdowns always show this full set; matching against loaded opportunities uses normalization in the dashboard.
 */
export const CANADA_PROVINCE_TO_CITIES: Record<string, readonly string[]> = {
  Alberta: [
    'Banff',
    'Brooks',
    'Calgary',
    'Edmonton',
    'Fort McMurray',
    'Grande Prairie',
    'Jasper',
    'Lake Louise',
    'Lethbridge',
    'Medicine Hat',
    'Red Deer',
    'Saint Albert',
  ],
  'British Columbia': [
    'Barkerville',
    'Burnaby',
    'Campbell River',
    'Chilliwack',
    'Courtenay',
    'Cranbrook',
    'Dawson Creek',
    'Delta',
    'Esquimalt',
    'Fort Saint James',
    'Fort Saint John',
    'Hope',
    'Kamloops',
    'Kelowna',
    'Kimberley',
    'Kitimat',
    'Langley',
    'Nanaimo',
    'Nelson',
    'New Westminster',
    'North Vancouver',
    'Oak Bay',
    'Penticton',
    'Powell River',
    'Prince George',
    'Prince Rupert',
    'Quesnel',
    'Revelstoke',
    'Rossland',
    'Trail',
    'Vancouver',
    'Vernon',
    'Victoria',
    'West Vancouver',
    'White Rock',
  ],
  Manitoba: [
    'Brandon',
    'Churchill',
    'Dauphin',
    'Flin Flon',
    'Kildonan',
    'Saint Boniface',
    'Swan River',
    'Thompson',
    'Winnipeg',
    'York Factory',
  ],
  'New Brunswick': [
    'Bathurst',
    'Caraquet',
    'Dalhousie',
    'Fredericton',
    'Miramichi',
    'Moncton',
    'Saint John',
  ],
  'Newfoundland and Labrador': [
    'Argentia',
    'Bonavista',
    'Channel-Port aux Basques',
    'Corner Brook',
    'Ferryland',
    'Gander',
    'Grand Falls–Windsor',
    'Happy Valley–Goose Bay',
    'Harbour Grace',
    'Labrador City',
    'Placentia',
    'Saint Anthony',
    "St. John's",
    'Wabana',
  ],
  'Northwest Territories': ['Fort Smith', 'Hay River', 'Inuvik', 'Tuktoyaktuk', 'Yellowknife'],
  'Nova Scotia': [
    'Baddeck',
    'Digby',
    'Glace Bay',
    'Halifax',
    'Liverpool',
    'Louisbourg',
    'Lunenburg',
    'Pictou',
    'Port Hawkesbury',
    'Springhill',
    'Sydney',
    'Yarmouth',
  ],
  Nunavut: ['Iqaluit'],
  Ontario: [
    'Bancroft',
    'Barrie',
    'Belleville',
    'Brampton',
    'Brantford',
    'Brockville',
    'Burlington',
    'Cambridge',
    'Chatham',
    'Chatham-Kent',
    'Cornwall',
    'Elliot Lake',
    'Etobicoke',
    'Fort Erie',
    'Fort Frances',
    'Gananoque',
    'Guelph',
    'Hamilton',
    'Iroquois Falls',
    'Kapuskasing',
    'Kawartha Lakes',
    'Kenora',
    'Kingston',
    'Kirkland Lake',
    'Kitchener',
    'Laurentian Hills',
    'London',
    'Midland',
    'Mississauga',
    'Moose Factory',
    'Moosonee',
    'Niagara Falls',
    'Niagara-on-the-Lake',
    'North Bay',
    'North York',
    'Oakville',
    'Orillia',
    'Oshawa',
    'Ottawa',
    'Parry Sound',
    'Perth',
    'Peterborough',
    'Picton',
    'Port Colborne',
    'Saint Catharines',
    'Saint Thomas',
    'Sarnia-Clearwater',
    'Sault Sainte Marie',
    'Scarborough',
    'Simcoe',
    'Stratford',
    'Sudbury',
    'Temiskaming Shores',
    'Thorold',
    'Thunder Bay',
    'Timmins',
    'Toronto',
    'Trenton',
    'Waterloo',
    'Welland',
    'West Nipissing',
    'Windsor',
    'Woodstock',
    'York',
  ],
  'Prince Edward Island': ['Borden', 'Cavendish', 'Charlottetown', 'Souris', 'Summerside'],
  Quebec: [
    'Asbestos',
    'Baie-Comeau',
    'Beloeil',
    'Cap-de-la-Madeleine',
    'Chambly',
    'Charlesbourg',
    'Châteauguay',
    'Chibougamau',
    'Côte-Saint-Luc',
    'Dorval',
    'Gaspé',
    'Gatineau',
    'Granby',
    'Havre-Saint-Pierre',
    'Hull',
    'Jonquière',
    'Kuujjuaq',
    'La Salle',
    'La Tuque',
    'Lachine',
    'Laval',
    'Lévis',
    'Longueuil',
    'Magog',
    'Matane',
    'Montreal',
    'Montréal-Nord',
    'Percé',
    'Port-Cartier',
    'Quebec',
    'Rimouski',
    'Rouyn-Noranda',
    'Saguenay',
    'Saint-Eustache',
    'Saint-Hubert',
    'Sainte-Anne-de-Beaupré',
    'Sainte-Foy',
    'Sainte-Thérèse',
    'Sept-Îles',
    'Sherbrooke',
    'Sorel-Tracy',
    'Trois-Rivières',
    "Val-d'Or",
    'Waskaganish',
  ],
  Saskatchewan: [
    'Batoche',
    'Cumberland House',
    'Estevan',
    'Flin Flon',
    'Moose Jaw',
    'Prince Albert',
    'Regina',
    'Saskatoon',
    'Uranium City',
  ],
  Yukon: ['Dawson', 'Watson Lake', 'Whitehorse'],
};

export const SORTED_CANADA_PROVINCES = Object.keys(CANADA_PROVINCE_TO_CITIES).sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })
);

/** Dedupe + A→Z city list for a province, or all cities when `all`. */
export function citiesForProvinceFilter(province: 'all' | string): string[] {
  if (province === 'all') {
    const set = new Set<string>();
    for (const cities of Object.values(CANADA_PROVINCE_TO_CITIES)) {
      for (const c of cities) set.add(c);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }
  const list = CANADA_PROVINCE_TO_CITIES[province];
  if (!list) return [];
  return [...list].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Subset of govt_opportunity.data used for Canada geo matching. */
export type CanadaGeoOppData = {
  state_province?: string;
  city?: string;
  location?: string;
  region?: string;
  place_of_performance?: string;
  /** Some feeds use `province` instead of `state_province`. */
  province?: string;
};

const ABBREV_TO_CANONICAL: Record<string, string> = {
  ab: 'Alberta',
  alta: 'Alberta',
  bc: 'British Columbia',
  mb: 'Manitoba',
  man: 'Manitoba',
  nb: 'New Brunswick',
  nl: 'Newfoundland and Labrador',
  nf: 'Newfoundland and Labrador',
  ns: 'Nova Scotia',
  nt: 'Northwest Territories',
  nwt: 'Northwest Territories',
  nu: 'Nunavut',
  on: 'Ontario',
  ont: 'Ontario',
  pe: 'Prince Edward Island',
  pei: 'Prince Edward Island',
  qc: 'Quebec',
  que: 'Quebec',
  pq: 'Quebec',
  sk: 'Saskatchewan',
  sask: 'Saskatchewan',
  yt: 'Yukon',
  yk: 'Yukon',
};

const CANONICAL_TO_SEARCH_ALIASES: Record<string, readonly string[]> = {
  Alberta: ['ab', 'alta'],
  'British Columbia': ['bc', 'b.c.'],
  Manitoba: ['mb', 'man'],
  'New Brunswick': ['nb'],
  'Newfoundland and Labrador': ['nl', 'nf'],
  'Northwest Territories': ['nt', 'nwt'],
  'Nova Scotia': ['ns'],
  Nunavut: ['nu', 'nunavut territory'],
  Ontario: ['on', 'ont', 'ontario (except ncr)'],
  'Prince Edward Island': ['pe', 'pei'],
  Quebec: ['qc', 'pq', 'que', 'quebec (except ncr)', 'quebec', 'québec'],
  Saskatchewan: ['sk', 'sask'],
  Yukon: ['yt', 'yk'],
};

function foldGeo(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\u2013|\u2014/g, '-')
    .replace(/[''`´]/g, "'")
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeGeoText(s: string): string {
  return foldGeo(s);
}

export function getProvinceSearchTokens(province: string): string[] {
  const canonical = province?.trim();
  if (!canonical) return [];
  const base = [canonical, ...(CANONICAL_TO_SEARCH_ALIASES[canonical] || [])];
  return [...new Set(base.map(foldGeo).filter(Boolean))];
}

/** True if query text should match this canonical province option (supports BC/ON/QC aliases). */
export function provinceOptionMatchesQuery(option: string, query: string): boolean {
  const q = foldGeo(query || '');
  if (!q) return true;
  const tokens = getProvinceSearchTokens(option);
  return tokens.some((t) => t.includes(q));
}

function oppHasGeographicText(d: CanadaGeoOppData): boolean {
  return [d.state_province, d.province, d.city, d.location, d.region, d.place_of_performance].some(
    (x) => typeof x === 'string' && x.trim().length > 0
  );
}

function resolveCanonicalProvince(d: CanadaGeoOppData): string | null {
  const rawParts = [d.state_province, d.province, d.region, d.location, d.place_of_performance].filter(
    (x): x is string => typeof x === 'string' && x.trim().length > 0
  );
  const stackedFold = rawParts.map(foldGeo).join(' | ');

  for (const canon of SORTED_CANADA_PROVINCES) {
    const fc = foldGeo(canon);
    if (stackedFold.includes(fc)) return canon;
  }

  for (const raw of rawParts) {
    const tokens = foldGeo(raw)
      .split(/[,\s/|]+/)
      .map((t) => t.replace(/[()]/g, '').trim())
      .filter(Boolean);
    for (const t of tokens) {
      if (ABBREV_TO_CANONICAL[t]) return ABBREV_TO_CANONICAL[t];
    }
  }

  return null;
}

function oppMatchesCity(d: CanadaGeoOppData, filterCity: string): boolean {
  const target = foldGeo(filterCity);
  const cityField = d.city?.trim();
  if (cityField && foldGeo(cityField) === target) return true;

  const loc = d.location?.trim() || '';
  if (loc) {
    const head = loc.split(',')[0]?.trim();
    if (head && foldGeo(head) === target) return true;
    if (foldGeo(loc).includes(target)) return true;
  }

  const pop = d.place_of_performance?.trim() || '';
  if (pop && foldGeo(pop).includes(target)) return true;

  return false;
}

/**
 * Client-side Canada province/city filter.
 * Opportunities with **no** geographic text in the usual fields are not excluded (nothing to match in DB).
 */
export function opportunityPassesCanadaGeoClientFilter(
  d: CanadaGeoOppData,
  filterProvince: 'all' | string,
  filterCity: 'all' | string
): boolean {
  if (filterProvince === 'all' && filterCity === 'all') return true;
  if (!oppHasGeographicText(d)) return true;

  if (filterProvince !== 'all' && filterCity === 'all') {
    return resolveCanonicalProvince(d) === filterProvince;
  }
  if (filterProvince === 'all' && filterCity !== 'all') {
    return oppMatchesCity(d, filterCity);
  }
  return resolveCanonicalProvince(d) === filterProvince && oppMatchesCity(d, filterCity);
}
